import type { AppointmentRecord } from "@/lib/types/appointment";
import type { Policy } from "@/lib/auth/AccessPolicy";
import { DateTime } from "luxon";
import { formatCurrency } from "@/lib/utils";

/**
 * Mobile accordion summary generators.
 * These produce concise one-line summaries for accordion triggers.
 */

/**
 * Details section summary: date, time range, master name
 * Example: "06.10 • 13:00-16:00 • Мастер Имя"
 */
export function getDetailsSummary(appointment?: AppointmentRecord | null): string {
  if (!appointment) return "—";

  const parts: string[] = [];

  // Date (DD.MM format)
  if (appointment.date) {
    const dt = DateTime.fromISO(appointment.date);
    if (dt.isValid) {
      parts.push(`${dt.day.toString().padStart(2, "0")}.${dt.month.toString().padStart(2, "0")}`);
    }
  }

  // Time range
  if (appointment.startTime && appointment.endTime) {
    parts.push(`${appointment.startTime}-${appointment.endTime}`);
  } else if (appointment.startTime) {
    parts.push(appointment.startTime);
  }

  // Master name
  if (appointment.staff?.name) {
    parts.push(appointment.staff.name);
  }

  return parts.length > 0 ? parts.join(" • ") : "—";
}

/**
 * Client section summary: name, phone (masked if policy requires)
 * Example: "Елена • +375 295674732" or "Елена • +••• •••• ••32"
 */
export function getClientSummary(
  appointment?: AppointmentRecord | null,
  policy?: Policy
): string {
  if (!appointment) return "—";

  const parts: string[] = [];

  // Client name
  if (appointment.client?.name) {
    parts.push(appointment.client.name);
  }

  // Phone (with masking if needed)
  const phonesVisible = policy?.timetable?.phonesVisible !== false;
  if (appointment.client?.phone) {
    const phone = phonesVisible
      ? appointment.client.phone
      : maskPhone(appointment.client.phone);
    parts.push(phone);
  }

  return parts.length > 0 ? parts.join(" • ") : "—";
}

/**
 * Payment section summary: status label, total amount
 * Example: "Оплачено • Итого: 118,00 BYN"
 */
export function getPaymentSummary(computedPayment?: {
  statusLabel?: string;
  totalAmountString?: string;
  total?: number;
}): string {
  if (!computedPayment) return "—";

  const parts: string[] = [];

  // Status label
  if (computedPayment.statusLabel) {
    parts.push(computedPayment.statusLabel);
  }

  // Total amount
  if (computedPayment.totalAmountString) {
    parts.push(`Итого: ${computedPayment.totalAmountString}`);
  } else if (typeof computedPayment.total === "number") {
    parts.push(`Итого: ${formatCurrency(computedPayment.total)}`);
  }

  return parts.length > 0 ? parts.join(" • ") : "—";
}

/**
 * Consumables section summary: count of items
 * Example: "3 items"
 */
export function getConsumablesSummary(appointment?: AppointmentRecord | null): string {
  if (!appointment) return "—";

  // @ts-ignore
  const count = appointment.consumables?.length ?? 0;
  // Return raw slug "no_consumables" for i18n translation in components
  if (count === 0) return "no_consumables";

  return `${count} items`;
}

/**
 * Status section summary: attendance status, service count
 * Example: "visited • 2 services"
 */
export function getStatusSummary(appointment?: AppointmentRecord | null): string {
  if (!appointment) return "—";

  const parts: string[] = [];

  // Raw attendance slug for i18n translation in components
  const attendance = appointment.attendance === 1
    ? "visited"
    : appointment.attendance === -1
      ? "no_show"
      : "waiting";
  parts.push(attendance);

  // Service count
  const serviceCount = appointment.services?.length ?? 0;
  if (serviceCount > 0) {
    parts.push(`${serviceCount} services`);
  }

  return parts.length > 0 ? parts.join(" • ") : "—";
}

/**
 * Visit history section summary: total visits, last visit date
 * Example: "12 виз. • посл.: 15.09"
 */
export function getVisitHistorySummary(appointment?: AppointmentRecord | null): string {
  if (!appointment) return "—";

  const parts: string[] = [];
  const success = appointment?.clientStats?.success_visits_count ?? 0;
  const fail = appointment?.clientStats?.fail_visits_count ?? 0;
  const total = success + fail;
  if (total > 0) parts.push(`${total} виз.`);

  return parts.length > 0 ? parts.join(" • ") : "—";
}

/**
 * Helper: mask phone number for privacy
 * Keeps last 2 digits visible
 */
function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 4) return "••••";
  const tail = digits.slice(-2);
  return `+••• •••• ••${tail}`;
}




/**
 * Changes section summary: placeholder for accordion
 * Since revisions are fetched async, we just show a static label
 */
export function getChangesSummary(): string {
  return "Changes history";
}
